import React, { useState } from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { BlogFormValues } from "src/models/Blog.model";
import ATMFilePickerWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import ATMTextEditor from "src/components/UI/atoms/formFields/ATMTextEditor/ATMTextEditor";
import { Chip, Box } from "@mui/material";

type Props = {
  formikProps: FormikProps<BlogFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const BlogForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const [tagInput, setTagInput] = useState("");

  const handleAddTag = () => {
    const newTag = tagInput.trim();
    if (newTag !== "" && !values.tags.includes(newTag)) {
      setFieldValue("tags", [...values.tags, newTag]);
      setTagInput("");
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setFieldValue(
      "tags",
      values.tags.filter((tag) => tag !== tagToDelete)
    );
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add Blog" : "Update Blog"}
        </div>
        <button
          type="button"
          className="px-3 py-1 border rounded hover:bg-gray-100"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <ATMTextField
          name="title"
          required
          value={values.title}
          placeholder="Enter a title"
          onChange={(e) => setFieldValue("title", e.target.value)}
          label="Title"
          onBlur={handleBlur}
        />

        {/* Tags with Add Button */}
        <div>
          <label className="block mb-1 text-sm font-medium text-slate-700">
            Tags
          </label>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <ATMTextField
                required
                name="tags"
                onBlur={handleBlur}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                size="small"
                placeholder="Enter a tag"
              />
            </div>
            <button
              type="button"
              onClick={handleAddTag}
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 rounded h-10 flex items-center"
            >
              Add
            </button>
          </div>

          <Box className="flex gap-2 mt-2 flex-wrap">
            {values.tags.map((tag, index) => (
              <Chip
                key={tag || index}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                sx={{
                  backgroundColor: "#e5e7eb", 
                  color: "#1f2937", 
                }}
              />
            ))}
          </Box>
        </div>

        {/* Description */}
        <ATMTextEditor
          label="Description"
          name="description"
          placeholder="Enter a description"
          required
          value={values.description}
          onChange={(value) => setFieldValue("description", value)}
        />

        {/* Image */}
        <ATMFilePickerWrapper
          name="image"
          label="Image"
          accept="image/*"
          onSelect={(value) => setFieldValue("image", value)}
          selectedFile={values.image}
        />
      </div>

      {/* Submit */}
      <ATMLoadingButton
        isLoading={isSubmitting}
        type={"submit" as "submit"} 
        className="bg-gray-800 hover:bg-gray-700 text-white"
      >
        Save
      </ATMLoadingButton>
    </div>
  );
};

export default BlogForm;
